/**
 * NOT-590 — grouping a service's component lines for display.
 *
 * A service that includes materials produces the service line PLUS one real,
 * priced line per component. Everything downstream — pricing, tax, margin, GL —
 * treats those as ordinary lines, which is exactly why none of it needs a
 * bundle-shaped special case.
 *
 * The only thing that varies is what the CUSTOMER sees:
 *   ITEMIZED   → each component shown priced on its own row
 *   ROLLED_UP  → one row for the service at parent + Σ components
 *
 * Totals are identical in both modes. If they ever differ, the rollup is wrong,
 * not the lines.
 */

import { chargesRevenue, rendersOnCustomerDocument, type LineEconomics } from './line-economics';

/**
 * Nullable throughout: these arrive from JSON payloads where an absent value is
 * `null`, not `undefined`. Requiring `undefined` would force every caller to
 * cast, which is exactly where a real type error would then hide.
 */
export interface ComponentGroupableLine {
  lineKey?: string | null;
  parentLineKey?: string | null;
  /** Only a SERVICE can be the parent of a bundle — see canCarryComponents. */
  type?: string | null;
  componentDisplayMode?: 'ITEMIZED' | 'ROLLED_UP' | null;
  isSelected?: boolean | null;
  lineTotal?: number | null;
  lineSubtotal?: number | null;
  /** How a component charges, when the relationship states it. */
  pricingTreatment?: 'included_in_service' | 'separate_charge' | null;
  /** The catalog flag the treatment defaults from — see componentBillsOwnLine. */
  chargeable?: boolean | null;
}

export interface ComponentGroup<T extends ComponentGroupableLine> {
  parent: T;
  components: T[];
  /** True when the customer should see one amount instead of the breakdown. */
  isRolledUp: boolean;
  /**
   * An OFFER's parts whatever the customer has chosen — what taking the offer
   * brings into the sale. `components` is what the offer LISTS, and a declined
   * offer lists nothing; this is what it COUNTS (`offerCountedLines`).
   */
  parts?: T[];
}

/**
 * Fold a flat line list into parents with their components attached.
 *
 * Lines that are not components, and orphaned component lines whose parent is
 * missing, come back as parents with no components — an orphan must still be
 * rendered and still counts toward the total. Silently dropping it would make
 * the visible lines disagree with the document total.
 */
/**
 * Can this line own component rows on a customer document?
 *
 * `parentLineKey` is data, not proof: an API or CSV caller sets it, and it
 * survives on a line whose type was changed afterwards. A SERVICE owns its
 * parts and a BUNDLE owns its members. Anything else is stale or forged.
 *
 * Deliberately weaker than `canCarryComponents` in the operations pricing
 * engine, which decides TAX and whose `PricingLineInput.type` is required. Here
 * `type` is optional across four different line shapes, and a document whose
 * payload omits it must keep rendering the bundle it has always rendered —
 * un-bundling a customer's PDF over a missing field would be a worse bug than
 * the one this guards. So: reject a type we can positively disprove, and leave
 * an absent one alone.
 */
export function canCarryComponents(type: unknown): boolean {
  if (type === null || type === undefined || String(type).trim() === '') return true;
  const normalized = String(type).trim().toUpperCase();
  return normalized === 'SERVICE' || normalized === 'BUNDLE';
}

export function groupComponentLines<T extends ComponentGroupableLine>(
  lines: T[],
): ComponentGroup<T>[] {
  const groups: ComponentGroup<T>[] = [];
  const byParentKey = new Map<string, ComponentGroup<T>>();
  // A component may be listed before its parent. Knowing up front which keys
  // belong to a real parent is what lets that case attach instead of being
  // mistaken for an orphan.
  //
  // A key claimed by more than one line belongs to none of them. The map below
  // is last-write-wins, so imported or API-authored lines sharing a `lineKey`
  // would silently hand the parts to whichever came last and print a bundle
  // nobody built. Lockstep with `resolveParts` in operations, which refuses the
  // same shape rather than guessing — and counts EVERY line, component rows
  // included: a part that reuses a service's key makes that key ambiguous
  // there, so it must here, or the quote would print a declined service's
  // parts out of the sale while approval repriced them as sold.
  const claimants = new Map<string, number>();
  for (const line of lines) {
    if (!line.lineKey) continue;
    claimants.set(line.lineKey, (claimants.get(line.lineKey) ?? 0) + 1);
  }
  const canAnchor = (line: T) =>
    Boolean(line.lineKey) && claimants.get(line.lineKey!) === 1 && canCarryComponents(line.type);
  const parentKeys = new Set(
    lines.filter((line) => !line.parentLineKey && canAnchor(line)).map((line) => line.lineKey!),
  );

  // ONE pass, in array order. Each card is anchored at its SERVICE's position
  // and its parts travel with it, so the caller's ordering survives grouping —
  // a two-pass version emitted every root first and appended orphans at the
  // end, which silently restacked the operator's lines and left the rendered
  // order disagreeing with the array that drag-and-drop indexes into.
  //
  // Parts listed BEFORE their service are held until it arrives rather than
  // anchoring the card early: the card's position has to be the service's, or
  // the rendered order and the reorder maths (which resolves a part to its
  // service) would disagree about where the card actually is.
  const heldForLaterParent = new Map<string, T[]>();

  for (const line of lines) {
    if (!line.parentLineKey) {
      const group: ComponentGroup<T> = {
        parent: line,
        components: line.lineKey ? (heldForLaterParent.get(line.lineKey) ?? []) : [],
        isRolledUp: line.componentDisplayMode === 'ROLLED_UP',
      };
      if (canAnchor(line)) {
        heldForLaterParent.delete(line.lineKey!);
        byParentKey.set(line.lineKey!, group);
      }
      groups.push(group);
      continue;
    }

    const group = byParentKey.get(line.parentLineKey);
    if (group) {
      group.components.push(line);
      continue;
    }

    if (parentKeys.has(line.parentLineKey)) {
      const held = heldForLaterParent.get(line.parentLineKey) ?? [];
      held.push(line);
      heldForLaterParent.set(line.parentLineKey, held);
      continue;
    }

    // Orphan: its parent was deleted or never persisted. Show it on its own,
    // where it actually sits, rather than hiding money the total includes.
    groups.push({ parent: line, components: [], isRolledUp: false });
  }

  return groups;
}

/**
 * The lines in the order grouped rendering emits them: each group's parent,
 * then its components.
 *
 * Anything that indexes into the rendered rows — dnd-kit's SortableContext
 * above all — has to be given THIS order rather than the raw array, or it maps
 * a drag onto whichever line happens to sit at that index in the array and
 * moves the wrong one.
 */
export function groupedLineOrder<T extends ComponentGroupableLine>(lines: T[]): T[] {
  return groupComponentLines(lines).flatMap((group) =>
    group.components.length > 0 ? [group.parent, ...group.components] : [group.parent],
  );
}

/**
 * What a group shows the customer as its price.
 *
 * ROLLED_UP folds the selected components in; ITEMIZED shows the service's own
 * amount because each component is priced on its own visible row. Unselected
 * optional components are excluded either way — they are offered, not sold.
 */
export function groupDisplayTotal<T extends ComponentGroupableLine>(
  group: ComponentGroup<T>,
  amountOf: (line: T) => number = (line) => line.lineTotal ?? 0,
): number {
  const parentAmount = amountOf(group.parent);
  if (!group.isRolledUp) return parentAmount;
  return group.components
    .filter((component) => component.isSelected !== false)
    .reduce((sum, component) => sum + amountOf(component), parentAmount);
}

/** Component lines the customer should NOT see, given the group's mode. */
export function hiddenComponents<T extends ComponentGroupableLine>(group: ComponentGroup<T>): T[] {
  return group.isRolledUp ? group.components : [];
}

/**
 * The parts of a bundle the customer is actually being sold.
 *
 * An unselected optional part is offered, not sold: it contributes nothing to
 * any total, so it must not print on a document that asks the customer to pay.
 */
export function soldComponents<T extends ComponentGroupableLine>(group: ComponentGroup<T>): T[] {
  return group.components.filter((component) => component.isSelected !== false);
}

/**
 * Who owns whom, for MONEY — `resolveParts` in `sold-lines.util.ts`, verbatim.
 *
 * Deliberately NOT `groupComponentLines`: that one is the display grouper and
 * only lets a top-level row anchor a card, which is right for rendering and
 * wrong here. The server has no such restriction, so a chain
 * `service → component → grandchild` had pricing drop both descendants while
 * the quote left the grandchild standing as an orphan. A key claimed by MORE
 * THAN ONE line — component rows included — belongs to none of them.
 */
function resolveSoldParents<T extends ComponentGroupableLine>(lines: T[]): Map<T, T[]> {
  const byLineKey = new Map<string, T>();
  const ambiguousKeys = new Set<string>();
  for (const line of lines) {
    const key = line.lineKey;
    if (!key) continue;
    if (byLineKey.has(key)) ambiguousKeys.add(key);
    else byLineKey.set(key, line);
  }

  const childrenOf = new Map<T, T[]>();
  for (const line of lines) {
    const parentKey = line.parentLineKey;
    if (!parentKey) continue;
    if (line.lineKey === parentKey) continue;
    if (ambiguousKeys.has(parentKey)) continue;
    const parent = byLineKey.get(parentKey);
    if (!parent) continue;
    if (!canCarryComponents(parent.type)) continue;
    const siblings = childrenOf.get(parent) ?? [];
    siblings.push(line);
    childrenOf.set(parent, siblings);
  }
  return childrenOf;
}

/**
 * The lines actually in the sale, with a declined service taking its parts
 * down — the rule every total answers to (`soldLines` in `sold-lines.util.ts`,
 * `declineParts` in the engine, `soldLines` in the pricing preview). A part's
 * own row still says selected when its service is declined, so a flat read off
 * the raw list counts lines the customer is not charged for.
 *
 * The server's algorithm, step for step: seed with every line the engine would
 * price (`isSelected !== false` — a required row written deselected through the
 * API is out of the sale there), then take each unsold parent's parts out. In
 * array order, so a dropped middle line takes ITS parts down too.
 */
export function soldLineSet<T extends ComponentGroupableLine & { isOptional?: boolean | null }>(
  lines: T[],
): Set<T> {
  const sold = new Set<T>(lines.filter((line) => line.isSelected !== false));
  const childrenOf = resolveSoldParents(lines);
  // Until nothing more can be removed — see `soldLines` in `sold-lines.util.ts`
  // for why one pass is order-dependent and this is not.
  let removedAny = true;
  while (removedAny) {
    removedAny = false;
    for (const [parent, parts] of childrenOf) {
      if (sold.has(parent)) continue;
      for (const part of parts) {
        if (sold.delete(part)) removedAny = true;
      }
    }
  }
  return sold;
}

export interface OptionalOfferSplit<T extends ComponentGroupableLine> {
  /** Every line in the sale whatever the customer chooses, minus offer parts. */
  required: T[];
  /**
   * Each thing the customer ticks, WITH the parts that hang off it.
   *
   * The parts travel here for RENDERING only — so a selected optional service
   * still prints as one bundle rather than losing its parts to the required
   * section. What they COUNT for is decided by `offerCountedLines`.
   */
  offers: ComponentGroup<T>[];
}

/**
 * The lines an offer contributes to the total.
 *
 * The parent is counted only if the customer took it, and its parts go with
 * it: a part is on the document because the service consumes it, and the
 * pricing engine prices the parts of a declined service at nothing
 * (`declineParts`). The parts' own rows still say selected — `customerApprove`
 * forces every non-optional line selected and rejects any attempt to deselect
 * one — so the cascade is derived here, never written to them.
 */
export function offerCountedLines<T extends ComponentGroupableLine>(
  group: ComponentGroup<T>,
  taken: boolean,
): T[] {
  return taken ? [group.parent, ...(group.parts ?? group.components)] : [];
}

/**
 * Split a document into what the customer is being sold and what they choose.
 *
 * Only a TOP-LEVEL line can be an offer. `isOptional` on a component says the
 * operator offered that part inside a service; it does not make the part a
 * separate thing the customer buys. Partitioning the flat list on `isOptional`
 * alone tore such a part away from its service, which left
 * `groupComponentLines` looking at a component whose parent was absent: it was
 * treated as an orphan and promoted to its own top-level row, so an unselected
 * add-on printed on the customer's quote as a standalone $6,000 line nobody was
 * buying, with a checkbox of its own.
 *
 * A part rides WITH its service: declined, the service takes its parts out of
 * the sale (the engine's `declineParts`), so they never land in `required`.
 * The parts' own rows are NOT deselected — `customerApprove` forces every
 * non-optional line selected and REJECTS an approval that submits one as
 * deselected — which is why this is a grouping decision and not a write.
 */
export function splitOptionalOffers<
  T extends ComponentGroupableLine & { isOptional?: boolean | null },
>(lines: T[]): OptionalOfferSplit<T> {
  const required: T[] = [];
  const offers: ComponentGroup<T>[] = [];

  const sold = soldLineSet(lines);

  for (const group of groupComponentLines(lines)) {
    // A DESCENDANT the sale does not include gets no row of its own.
    //
    // The display grouper anchors a card on a top-level row, so a line whose
    // parent is itself a component — `service → kit → fitting` — never attaches
    // and comes back as its own group. Rendered as-is, a declined service's
    // grandchild printed on the quote and the PDF as a standalone line the
    // customer would be charged for, while `soldLineSet` (and the server) had
    // already taken it out of every total. Asked of the sold set, not of the
    // grouper, because ownership for MONEY is the whole chain.
    if (group.parent.parentLineKey && !sold.has(group.parent)) continue;

    // A part is sold exactly as the operator configured it, under the server's
    // rule — never under a checkbox of its own. `isSelected !== false` is that
    // rule verbatim (`isLineSold`): a row the engine prices as out of the sale
    // is not printed as one the customer is charged for.
    const parts = group.components.filter((component) => component.isSelected !== false);

    // A REQUIRED line stored deselected is not a shape the editor can make —
    // `customerApprove` refuses to accept one — but an import or an API caller
    // can, and the engine prices it at nothing. It is neither charged nor
    // offered, so it is neither, and its parts go with it.
    if (!group.parent.isOptional && group.parent.isSelected === false) continue;

    if (group.parent.isOptional) {
      // Parts ride WITH the offer. A taken offer lists them as its bundle; a
      // declined one lists nothing — there is no sale of the parts either, the
      // engine prices them at nothing — and they are never promoted into the
      // base sale as lines the customer is charged for. `parts` keeps them
      // for `offerCountedLines`, so ticking the offer counts them again.
      const sold = group.parent.isSelected !== false;
      offers.push({ ...group, components: sold ? parts : [], parts });
      continue;
    }

    required.push(group.parent, ...parts);
  }

  return { required, offers };
}

/**
 * A component line, plus the fields that decide whether it charges for itself
 * and whether it may be printed. `chargesRevenue` and
 * `rendersOnCustomerDocument` own those questions — this only names what they
 * read.
 */
type FoldableComponentLine = ComponentGroupableLine &
  Pick<
    LineEconomics,
    'chargeable' | 'pricingTreatment' | 'visibleToCustomer' | 'hiddenFromCustomer' | 'showInDetail'
  >;

/**
 * A part comes OUT of the service's fold onto a line of its own when it HAS A
 * PRICE. A part that carries only COST stays folded.
 *
 * The driver is what the part contributes, not what type it is. A part that
 * contributes revenue is something the customer is being charged for, so it is
 * named and priced on a line they can read. A cost-only part is already paid
 * for through the service that installs it: unfolding it would put a name on
 * the document with no charge behind it, and the customer reads the service.
 *
 * EQUIPMENT is the one exception, and it is deliberate: equipment is shown even
 * when its line charges nothing. What the customer is having INSTALLED is the
 * substance of the offer — they should see the unit they are getting whether or
 * not it is billed separately — whereas a consumable recovered through the
 * service is noise on an offer. Shown with no amount against it, not at $0.00;
 * `rowShowsMonetaryAmount` owns that half.
 *
 * SILENCE IS NOT A PRICE. `chargesRevenue` reads an absent flag as chargeable,
 * which is right for MONEY — every document saved before the flag existed must
 * keep billing what it always billed — and wrong for LAYOUT: a payload that
 * never states chargeability at all (`PublicLineItem` has no such field) would
 * unfold every part of every legacy rolled-up service, tearing apart the single
 * amount the operator chose to present and the customer approved. So a part
 * unfolds only where the data actually says it charges for itself. The default
 * for a new part says so explicitly either way — see
 * `NEW_COMPONENT_PRICING_TREATMENT`.
 *
 * This is a LAYOUT decision and nothing else. An unfolded line moves from the
 * service row's `lines` to its own row's `lines`; every raw line still appears
 * in exactly one row, so anything summing over the rows gets the figure it got
 * before — and the document totals never read the rows at all.
 *
 * A part the customer must not READ has no row of its own to unfold onto, so it
 * stays folded whatever it charges. That is not a visibility rule sneaking in
 * here — it is the money invariant. `customerVisibleRows` drops a hidden row
 * ENTIRELY, so unfolding a hidden part that charges took its amount out of every
 * printed row while the subtotal and total kept counting it: the customer read a
 * $100 service over a $116 subtotal with nothing on the page for the $16. Folded,
 * the same money is presented inside the service's row, which is where a
 * rolled-up service already presents everything it covers, and the printed rows
 * foot to the total again. Nothing about the total moves either way.
 */
export function unfoldsOntoItsOwnLine(line: FoldableComponentLine): boolean {
  if (!rendersOnCustomerDocument(line)) return false;
  const isEquipment =
    String(line.type ?? '')
      .trim()
      .toUpperCase() === 'EQUIPMENT';
  if (isEquipment) return true;
  // The relationship's TREATMENT when it states one, before `chargesRevenue`,
  // which calls a line with no `type` "not a part" and therefore billed. That
  // is right for a service and wrong for the legacy component snapshots that
  // state a treatment but no type: an `included_in_service` row was unfolding
  // onto a line of its own, and the card then described a document printing a
  // part whose money is inside the service's price. Same rule, same order, as
  // `componentBillsOwnLine`.
  if (line.pricingTreatment === 'included_in_service') return false;
  if (line.pricingTreatment === 'separate_charge') return true;
  if (line.pricingTreatment == null && line.chargeable == null) return false;
  return chargesRevenue(line);
}

export interface CustomerFacingLine<T extends ComponentGroupableLine> {
  /** The line whose name, description and unit this row shows. */
  line: T;
  /**
   * Every raw line this row stands for — just `[line]` normally, and the
   * service plus its selected components on a ROLLED_UP row.
   *
   * Callers derive the row's money by summing over THESE, so a rolled-up row
   * reports the same subtotal, tax and total the raw lines already contribute
   * to the document. Nothing is re-derived and nothing can drift.
   */
  lines: T[];
  /** Names of the parts folded into this row, for a "what's included" note. */
  includedNames: { name: string; quantity: number; isOptional: boolean }[];
}

/**
 * The rows a CUSTOMER document should print.
 *
 * ROLLED_UP services collapse into one row whose amount is the service plus its
 * selected components; ITEMIZED services print every row as-is.
 *
 * What a ROLLED_UP service actually folds is its COST-ONLY parts. A part that
 * has a price of its own — and equipment whatever it is priced at — unfolds
 * onto its own line with its own description; see `unfoldsOntoItsOwnLine`. The
 * money travels with the line out of the parent row, so the rows still account
 * for each raw line exactly once.
 *
 * A part the operator offered inside a service but did not select is left out
 * entirely — no row, and no line in the "what's included" note. It is worth
 * nothing on this sale, so printing it puts a price on the customer's document
 * that no total accounts for, and invites them to ask what they are paying for.
 * Top-level optional lines are a different thing and are not touched here: the
 * customer chooses those, and the document has to show them to be chosen.
 *
 * Use this for RENDERING ONLY. Subtotal, tax and grand total must keep being
 * computed from the raw lines: they already sum to the same figures, and
 * re-deriving them from collapsed rows would mean summing per-line tax
 * snapshots, which is not a safe operation.
 */
export function customerFacingLines<
  T extends FoldableComponentLine & {
    name?: string;
    quantity?: number;
    isOptional?: boolean | null;
  },
>(lines: T[]): CustomerFacingLine<T>[] {
  const groups = groupComponentLines(lines);
  const sold = soldLineSet(lines);
  const out: CustomerFacingLine<T>[] = [];

  for (const group of groups) {
    // A DESCENDANT the sale does not include gets no row of its own.
    //
    // The display grouper anchors a card on a top-level row, so a line whose
    // parent is itself a component — `service → kit → fitting` — never attaches
    // and comes back as its own group. Rendered as-is, a declined service's
    // grandchild printed on the quote and the PDF as a standalone line the
    // customer would be charged for, while `soldLineSet` (and the server) had
    // already taken it out of every total. Asked of the sold set, not of the
    // grouper, because ownership for MONEY is the whole chain.
    if (group.parent.parentLineKey && !sold.has(group.parent)) continue;

    // An unselected optional part is offered, not sold. It contributes nothing
    // to the document total in either display mode, so it gets no row and no
    // mention — see the note above.
    const included = soldComponents(group);

    // A service the customer is not being sold has no bundle to roll anything
    // into. Collapsing anyway would print its name over money that is coming
    // from a part sold on its own — the engine and the preview both price that
    // part standalone, so the document would show a service nobody bought and
    // still not foot. Parent and part carry independent optional/selected
    // toggles, so this state is one click away in the editor.
    if (!group.isRolledUp || group.parent.isSelected === false) {
      // ITEMIZED prints every part on a row of its own — except one the customer
      // must not read, which has no row to print on. Its money rides in the
      // service's row instead of dropping off the page while the subtotal keeps
      // counting it; same invariant as `unfoldsOntoItsOwnLine`.
      //
      // Only while the service is actually being SOLD. With the parent declined
      // there is no row of its own that the customer is paying for, so folding
      // there would print money under a service they said no to.
      const sold = group.parent.isSelected !== false;
      const folded = sold ? included.filter((line) => !rendersOnCustomerDocument(line)) : [];
      out.push({ line: group.parent, lines: [group.parent, ...folded], includedNames: [] });
      for (const component of included) {
        if (folded.includes(component)) continue;
        out.push({ line: component, lines: [component], includedNames: [] });
      }
      continue;
    }

    // A part that has a price of its own — and equipment either way — is
    // unfolded onto its own line, carrying its own description, instead of a
    // bullet inside the service's row. The service keeps the cost-only parts:
    // its price already covers them, so they stay in its row and in its
    // "what's included" note.
    const parentIsBundle = String(group.parent.type ?? '').trim().toUpperCase() === 'BUNDLE';
    // A catalog BUNDLE configured as one line owns every ordinary member,
    // including member SERVICES and equipment. Only an explicit billed-on-top
    // relationship escapes onto a customer line. Applying the ordinary service
    // rule here let revenue-bearing member services and equipment unfold even
    // though their price was already inside the bundle parent.
    const unfoldsFromThisGroup = (component: T) =>
      parentIsBundle
        ? component.pricingTreatment === 'separate_charge' &&
          rendersOnCustomerDocument(component)
        : unfoldsOntoItsOwnLine(component);
    const folded = included.filter((component) => !unfoldsFromThisGroup(component));
    const unfolded = included.filter((component) => unfoldsFromThisGroup(component));

    out.push({
      line: group.parent,
      lines: [group.parent, ...folded],
      // The "what's included" note obeys the same visibility rule a row does.
      // A folded part the customer must not read — a cost-only material, or
      // anything the eye toggle hides — does not come back as a bullet; its
      // money stays in the service's row either way.
      includedNames: parentIsBundle
        ? []
        : folded.filter(rendersOnCustomerDocument).map((component) => ({
            name: component.name ?? '',
            quantity: component.quantity ?? 1,
            isOptional: component.isOptional ?? false,
          })),
    });
    for (const component of unfolded) {
      out.push({ line: component, lines: [component], includedNames: [] });
    }
  }

  return out;
}

/**
 * Drop the rows a CUSTOMER document must not print.
 *
 * `rendersOnCustomerDocument` owns the rule and is asked about EVERY row, parts
 * included. It used to be waved past for anything with a parent, on the
 * reasoning that a part which still had a row of its own must have been
 * itemised and therefore billed — but visibility and chargeability are separate
 * questions now. A service's equipment prints whether or not it is charged for,
 * and a non-chargeable material never prints, parent or no parent.
 *
 * Money is untouched. Subtotal, tax and total keep being summed from the raw
 * lines, so a hidden line is hidden from the page and still on the bill — and
 * an included part, printed with no amount against it, puts nothing back on it.
 */
export function customerVisibleRows<
  T extends ComponentGroupableLine & {
    chargeable?: boolean | null;
    hiddenFromCustomer?: boolean | null;
    visibleToCustomer?: boolean | null;
  },
>(rows: CustomerFacingLine<T>[]): CustomerFacingLine<T>[] {
  return rows.filter((row) => rendersOnCustomerDocument(row.line));
}

/**
 * Whether a CUSTOMER row prints a monetary amount at all — NOT-637.
 *
 * The reported bug: a 40-gal water heater included in a $2,950 service printed
 * as its own row at `$0.00`. `$0.00` is a price, and the customer read it as
 * one — the heater was free, so what was the $2,950 for? The heater is not
 * free; it is paid for inside the service that installs it, which is the
 * sentence `INCLUDED_PRICE_DISPLAY` puts in the cell instead. The row still
 * prints: what the customer is having installed is the substance of the offer
 * (see `unfoldsOntoItsOwnLine`), and only the amount changes.
 *
 * `SPEC-line-item-model.md` has said this all along — "Visible +
 * non-chargeable → show as 'Included', or with no monetary amount" — and
 * `showsMonetaryAmount` has expressed it all along. Nothing called it.
 *
 * ASKED OF `row.lines`, not of `row.line`, and that is the whole reason this
 * wrapper exists rather than the renderers calling `showsMonetaryAmount`
 * directly. A row is not always one line: a ROLLED_UP service stands for itself
 * PLUS every part folded into it, including a part that CHARGES and is hidden,
 * whose amount rides in the service's row precisely so the page keeps footing.
 * Every renderer already sums that row's price, tax and total over `row.lines`,
 * so the predicate that decides whether to print those figures has to range
 * over the same set. Then it is arithmetic rather than judgement: a row goes
 * wordless only when every raw line behind it charges nothing, so the amount
 * being suppressed is provably $0 and no printed figure can go missing.
 *
 * Ask `row.line` instead and the two sets come apart — the answer would be
 * about the parent while the figures are about the group. Today a parent is
 * always a SERVICE, which always charges, so nothing visibly breaks; the first
 * fold that puts a charging line under a non-charging one is where it would,
 * and by then the bug is on a customer's PDF.
 *
 * Rendering only. Subtotal, tax and grand total keep summing the raw lines,
 * where a non-chargeable line already contributes $0 — so a row that stops
 * showing an amount cannot move a total, in either direction.
 */
export function rowShowsMonetaryAmount<T extends FoldableComponentLine>(
  row: CustomerFacingLine<T>,
): boolean {
  return row.lines.some((line) => chargesRevenue(line));
}

/**
 * Translate a drag between two RENDERED rows into a move on the raw array.
 *
 * The two are not the same coordinate space. Grouping draws a service's parts
 * inside its card, and which card a part belongs to follows `parentLineKey`,
 * not array adjacency — so the row above another on screen can sit anywhere in
 * the array. Handing dnd-kit's ids straight to `arrayMove` therefore moved a
 * different line than the one dragged, or no-oped and snapped the row back.
 *
 * Two moves are meaningful, and both are a single `arrayMove`:
 *
 * - two parts of the SAME service → reorder them within that card; their order
 *   inside the group follows their order in the array.
 * - anything else → move whole cards, by moving their ANCHORS (the service
 *   line, or a standalone line, that the card is drawn around). A card's
 *   position follows its anchor's position, and its parts travel with it
 *   wherever they sit in the array, so the anchor is the only element that has
 *   to move.
 *
 * Returns null when the drag cannot move anything — dropping a part onto its
 * own service, for instance.
 */
export function groupedReorder<T extends ComponentGroupableLine>(
  lines: T[],
  activeIndex: number,
  overIndex: number,
): { from: number; to: number } | null {
  const active = lines[activeIndex];
  const over = lines[overIndex];
  if (!active || !over || activeIndex === overIndex) return null;

  // Parts of one service: they reorder among themselves.
  if (active.parentLineKey && active.parentLineKey === over.parentLineKey) {
    return { from: activeIndex, to: overIndex };
  }

  const anchorIndexOf = (line: T, index: number) => {
    if (!line.parentLineKey) return index;
    const parent = lines.findIndex(
      (candidate) => !candidate.parentLineKey && candidate.lineKey === line.parentLineKey,
    );
    // An orphan is its own anchor — it is drawn as a card in its own right.
    return parent >= 0 ? parent : index;
  };

  const from = anchorIndexOf(active, activeIndex);
  const to = anchorIndexOf(over, overIndex);
  return from === to ? null : { from, to };
}
